import React from 'react';

import { Box, Flex, Space, Text, Title } from '@mantine/core';
import { hideNotification, showNotification } from '@mantine/notifications';
import { IconCheck, IconExclamationMark, IconX } from '@tabler/icons-react';
import { AxiosError, HttpStatusCode } from 'axios';
import { UpgradePlanButton } from 'components/UpgradePlanButton';
import i18next from 'i18next';
import { UserService } from 'services/user-service';
import { RESOURCE_BATCH_UPLOAD_NOTIF_ID } from 'shared/constants/notification-ids';
import { ServerError } from 'shared/constants/server-errors';
import { PRIMARY_SHADE_WHITE } from 'shared/constants/theme';
import { UploadResourceProgressEvent } from 'shared/interfaces/Resource';
import { UserAccess } from 'shared/interfaces/User';
import { getResourceUploadProgress } from 'shared/utils/resource';

const AUTO_CLOSE_MILLISECONDS = 5000;

export const NotificationService = {
  startAction: (notificationId: string, translatedMessageKey: string) => {
    // Used to hide previous noticiation for the same action
    hideNotification(notificationId);
    showNotification({
      id: notificationId,
      autoClose: false,
      withCloseButton: false,
      message: i18next.t(translatedMessageKey),
      styles: (theme) => ({
        description: {
          color: theme.other.text.default,
          fontSize: theme.fontSizes.sm
        },
        icon: {
          color: theme.primaryColor[PRIMARY_SHADE_WHITE],
          width: 20,
          height: 20
        },
        loader: {
          width: '20px !important',
          height: '20px !important'
        }
      }),
      loading: true
    });
  },
  endAction: (
    success: boolean | undefined, // undefined means to show a warning
    notificationId: string,
    translatedMessageKey: string,
    error?: AxiosError,
    forGuest = false
  ) => {
    const isUsageError = error?.response?.status === HttpStatusCode.PaymentRequired;
    const message = isUsageError ? processUsageErrors(error, forGuest) : i18next.t(translatedMessageKey);
    hideNotification(notificationId);
    showNotification({
      id: notificationId,
      autoClose: AUTO_CLOSE_MILLISECONDS,
      message: message,
      color: getColorFromSuccess(isUsageError ? undefined : success),
      icon: getIconFromSuccess(isUsageError ? undefined : success),
      styles: (theme) => ({
        description: {
          color: theme.other.text.default,
          fontSize: theme.fontSizes.sm
        },
        icon: {
          width: 20,
          height: 20
        }
      })
    });
  },
  showResult: (
    success: boolean | undefined,
    translatedMessageKey: string,
    error?: AxiosError,
    forGuest = false,
    notificationId?: string
  ) => {
    const isUsageError = error?.response?.status === HttpStatusCode.PaymentRequired;
    const message = isUsageError ? processUsageErrors(error, forGuest) : i18next.t(translatedMessageKey);
    if (notificationId) hideNotification(notificationId);
    showNotification({
      id: notificationId,
      autoClose: AUTO_CLOSE_MILLISECONDS,
      message: message,
      color: getColorFromSuccess(isUsageError ? undefined : success),
      icon: getIconFromSuccess(isUsageError ? undefined : success),
      styles: (theme) => ({
        description: {
          color: theme.other.text.default,
          fontSize: theme.fontSizes.sm
        },
        icon: {
          width: 20,
          height: 20
        }
      })
    });
  },
  batchUploadProgression: (
    events: UploadResourceProgressEvent[],
    notificationId = RESOURCE_BATCH_UPLOAD_NOTIF_ID,
    titleKey = 'FEED_BACK.RESOURCES.LOADING.BATCH_UPLOAD'
  ) => {
    hideNotification(notificationId);
    showNotification({
      id: notificationId,
      autoClose: false,
      color: 'white',
      withCloseButton: false,
      message: (
        <Box>
          <Title order={6}>{i18next.t(titleKey)}</Title>
          {events.map((event, key) => getResourceUploadProgress(event, key))}
        </Box>
      ),
      styles: () => ({
        root: {
          maxHeight: '100vh !important',
          '::before': {
            backgroundColor: 'transparent !important'
          }
        }
      })
    });
  },
  hideNotification: (notificationId: string) => hideNotification(notificationId)
};

const getColorFromSuccess = (success: boolean | undefined) => {
  if (success === undefined) return 'yellow';
  return success ? 'green' : 'red';
};

const getIconFromSuccess = (success: boolean | undefined) => {
  if (success === undefined) return <IconExclamationMark style={{ width: 15, height: 15 }} />;
  return success ? <IconCheck style={{ width: 15, height: 15 }} /> : <IconX style={{ width: 15, height: 15 }} />;
};

const processUsageErrors = (error: AxiosError, forGuest = false) => {
  let usageError = (error.response?.data as ServerError).message;
  const canManagePlan =
    UserService.hasUserAccessWithMinRole(UserAccess.SUPER_ADMIN) && !UserService.isCustomOrganisationMember();
  usageError = usageError + (forGuest ? '_GUEST' : '');

  return (
    <Flex direction="row" align="center" justify="space-between">
      <Text>{i18next.t(usageError)}</Text>
      {canManagePlan && !forGuest ? (
        <>
          <Space w={10} />
          <UpgradePlanButton />
        </>
      ) : null}
    </Flex>
  );
};
